// SearchPanel — boards 17 (desktop, 460px) and 18 (phone). Every colour, line, radius and face is a house token.

.panel {
    display: flex;
    flex-direction: column;
    min-width: 0;
    font-family: var(--tl-font);
}

// The desktop dropdown: 6px under the field, the field's ground, its own border and shadow.
.panel[data-layout='dropdown'] {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    padding-top: 4px;
    background: var(--tl-surface);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-chrome-lg);
    box-shadow: 0 18px 40px color-mix(in srgb, var(--tl-shadow-ink) 45%, transparent);
}

.scroll {
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
}

.panel[data-layout='dropdown'] .scroll {
    max-height: min(70vh, 640px);
}

.panel[data-layout='full'] .scroll {
    flex: 1;
}

.sec {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--tl-border);
}

.bare {
    border-bottom: none;
}

.head {
    display: flex;
    align-items: center;
    gap: 8px;
}

.name {
    margin: 0;
    font-family: var(--tl-font-label);
    font-size: 9.5px;
    font-weight: normal;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--tl-muted);
}

.act {
    margin-left: auto;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-size: 11.5px;
    color: var(--tl-muted);
    cursor: pointer;

    &:hover {
        color: var(--tl-text);
    }

    &:focus-visible {
        outline: 1px solid var(--tl-accent);
        outline-offset: 2px;
    }
}

.chips {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
}

// The `+N` chip's measure: out of the flow and out of sight.
.probe {
    position: absolute;
    visibility: hidden;
    pointer-events: none;
}

// Facet groups: a name column, then the chips (desktop).
.group {
    display: grid;
    grid-template-columns: 62px 1fr;
    gap: 8px;
    align-items: start;
}

.groupName {
    padding-top: 5px;
}

// Recent: one row of pills and its own ×.
.recent {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 -6px;
    padding: 5px 6px;
}

.recentPick {
    display: flex;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
    gap: 6px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;

    &:focus-visible {
        outline: 1px solid var(--tl-accent);
        outline-offset: 2px;
    }
}

.recentX {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 28px;
    height: 28px;
    margin-left: auto;
    padding: 0;
    border: none;
    background: none;
    color: var(--tl-dim);
    cursor: pointer;

    &:hover,
    &:focus-visible {
        color: var(--tl-text);
    }
}

// Rows: thumbnail, the phrase with the typed word in bold, a kind label.
.rows,
.closest {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    padding: 10px 14px;
    list-style: none;
}

.closest {
    padding: 0;
}

.row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 -6px;
    padding: 5px 6px;
    border-radius: var(--tl-radius-chrome-sm);
    font-size: 13.5px;
    color: var(--tl-muted);
    cursor: pointer;

    b {
        font-weight: 500;
        color: var(--tl-text);
    }
}

.rowOn {
    color: var(--tl-text);
    background: color-mix(in srgb, var(--tl-accent) 8%, transparent);
}

.rowLabel {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.kind {
    margin-left: auto;
    flex: none;
    font-family: var(--tl-font-label);
    font-size: 9px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--tl-dim);
}

.thumb {
    flex: none;
    width: 26px;
    height: 34px;
    overflow: hidden;
    border-radius: var(--tl-radius-art-xs);

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.thumbEmpty {
    box-sizing: border-box;
    border: 1px solid var(--tl-border-strong);
}

// The Make row, always last.
.make {
    display: flex;
    align-items: center;
    gap: 10px;
    box-sizing: border-box;
    width: 100%;
    margin: 0;
    padding: 11px 14px;
    border: none;
    border-radius: 0 0 var(--tl-radius-chrome-lg) var(--tl-radius-chrome-lg);
    background: color-mix(in srgb, var(--tl-accent) 5%, transparent);
    font-family: var(--tl-font);
    font-size: 13.5px;
    text-align: left;
    color: var(--tl-muted);

    b {
        font-weight: 500;
        color: var(--tl-text);
    }
}

button.make {
    cursor: pointer;

    &:focus-visible {
        outline: 1px solid var(--tl-accent);
        outline-offset: -2px;
    }
}

.makeLit {
    background: var(--tl-accent);
    color: var(--tl-accent-ink);

    b {
        color: var(--tl-accent-ink);
    }
}

.makeText {
    min-width: 0;
}

.ghostFrame {
    flex: none;
    box-sizing: border-box;
    width: 26px;
    height: 34px;
    border: 1px dashed var(--tl-accent-line);
    border-radius: var(--tl-radius-art-xs);
}

.makeLit .ghostFrame {
    border-color: var(--tl-accent-ink);
}

.hint {
    margin-left: auto;
    flex: none;
    font-family: var(--tl-font-label);
    font-size: 10px;
    letter-spacing: 0.1em;
    color: var(--tl-dim);
}

.makeLit .hint {
    color: var(--tl-accent-ink);
}

// The phone: the panel is the body of the sheet the field stands in, not a card under it.
.panel[data-layout='full'],
.panel[data-layout='half'] {
    flex: 1;
    min-height: 0;

    .sec {
        gap: 8px;
        padding: 12px 16px;
    }

    .act {
        font-size: 12.5px;
    }

    .group {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .groupName {
        padding-top: 0;
    }

    .rows {
        padding: 12px 16px;
    }

    .closest {
        padding: 0;
    }

    .row {
        min-height: 48px;
        margin: 0;
        padding: 4px 0;
        font-size: 14.5px;
    }

    .thumb {
        width: 30px;
        height: 40px;
    }

    .make {
        min-height: 58px;
        padding: 12px 16px;
        border-top: 1px solid var(--tl-border);
        border-radius: 0;
        font-size: 14px;
    }

    .ghostFrame {
        width: 28px;
        height: 38px;
    }
}

// Half sheet: the Make row sits on the sheet's own ground; a panel taller than the screen scrolls in its one middle.
.panel[data-layout='half'] {
    flex: 0 1 auto;

    .make {
        min-height: 48px;
        padding: 6px 16px;
        border-top: none;
        background: none;
    }

    .makeLit {
        background: var(--tl-accent);
    }
}
